import React, { useState, useRef, useEffect, useCallback } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './AddTaskModal.module.css';
import { FaTimes, FaSave, FaPlus, FaSearch } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';

const AddTaskModal = ({ onClose, onUpdate, employees = [], editingTask = null }) => {
  const { isDarkMode } = useTheme();
  const { employeeData } = useUserProfile();
  const modalRef = useRef(null);
  const isEditing = !!editingTask;

  const [formData, setFormData] = useState({
    title: editingTask?.title || '',
    description: editingTask?.description || '',
    priority: editingTask?.priority || 'medium',
    status: editingTask?.status || 'pending',
    dueDate: editingTask?.dueDate
      ? (editingTask.dueDate.toDate ? editingTask.dueDate.toDate() : new Date(editingTask.dueDate))
          .toISOString().split('T')[0]
      : '',
  });

  const [selectedAssignees, setSelectedAssignees] = useState(
    editingTask?.assignees || []
  );
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleAssignee = (emp) => {
    setSelectedAssignees(prev => {
      const exists = prev.find(a => a.id === emp.id);
      if (exists) {
        return prev.filter(a => a.id !== emp.id);
      }
      return [...prev, { id: emp.id, name: `${emp.eName} ${emp.eSurname}` }];
    });
  };

  const removeAssignee = (empId) => {
    setSelectedAssignees(prev => prev.filter(a => a.id !== empId));
  };

  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.eName} ${emp.eSurname}`.toLowerCase();
    return fullName.includes(assigneeSearch.toLowerCase());
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Görev başlığı zorunludur');
      return;
    }

    if (selectedAssignees.length === 0) {
      toast.error('En az bir kişi atanmalıdır');
      return;
    }

    setIsSaving(true);

    try {
      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        status: formData.status,
        assignees: selectedAssignees,
        assigneeName: selectedAssignees.map(a => a.name).join(', '),
        assigneeId: selectedAssignees.length === 1 ? selectedAssignees[0].id : null,
        dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate)) : null,
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        const taskRef = doc(db, 'Tasks', editingTask.id);
        await updateDoc(taskRef, taskData);
        toast.success('Görev güncellendi');
      } else {
        taskData.createdAt = serverTimestamp();
        taskData.createdBy = employeeData
          ? `${employeeData.eName || ''} ${employeeData.eSurname || ''}`.trim()
          : 'Admin';

        // Ana görevi oluştur
        const mainTaskRef = await addDoc(collection(db, 'Tasks'), taskData);

        // Görev başlığında "proje" geçiyorsa muhasebe ekibine otomatik görev oluştur
        const isProjectTask = formData.title.toLowerCase().includes('proje') || 
                             formData.description.toLowerCase().includes('proje');

        if (isProjectTask) {
          // Muhasebe ekibi üyeleri
          const accountingTeam = [
            { name: 'AYGÜL DAĞ CEYLAN', email: 'aygul.dag@habcare.com.tr' },
            { name: 'ÖZGE YIKIMLAR', email: 'ozge.yikimlar@habcare.com.tr' },
            { name: 'MELİSA ŞENER', email: 'melisa.sener@habcare.com.tr' }
          ];

          // Muhasebe ekibi üyelerini employees listesinden bul
          const accountingEmployees = employees.filter(emp => 
            accountingTeam.some(member => 
              `${emp.eName} ${emp.eSurname}`.toUpperCase().includes(member.name.toUpperCase()) ||
              emp.eMail?.toLowerCase().includes(member.email.toLowerCase())
            )
          );

          console.log('Bulunan muhasebe çalışanları:', accountingEmployees);

          // Her muhasebe üyesi için ayrı görev oluştur
          const accountingTaskPromises = accountingEmployees.map(async (emp) => {
            const accountingTaskData = {
              title: `${formData.title.trim()} - Muhasebe İşlemleri`,
              description: `${formData.description.trim()}\n\nBu görev yeni proje için muhasebe işlemlerini kapsamaktadır.`,
              priority: formData.priority,
              status: 'pending',
              assignees: [{ id: emp.id, name: `${emp.eName} ${emp.eSurname}` }],
              assigneeName: `${emp.eName} ${emp.eSurname}`,
              assigneeId: emp.id,
              dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate)) : null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              createdBy: employeeData
                ? `${employeeData.eName || ''} ${employeeData.eSurname || ''}`.trim()
                : 'Admin',
              parentTaskId: mainTaskRef.id, // Ana görevle ilişkilendir
              isAccountingTask: true, // Muhasebe görevi olduğunu işaretle
              accountingGroup: `${formData.title.trim()}_${Date.now()}` // Grup kimliği
            };

            return addDoc(collection(db, 'Tasks'), accountingTaskData);
          });

          await Promise.all(accountingTaskPromises);
          
          toast.success(`Görev oluşturuldu ve ${accountingEmployees.length} muhasebe üyesine otomatik görev atandı`);
        } else {
          toast.success('Görev oluşturuldu');
        }
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Görev kaydedilirken hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`${styles.overlay} ${isDarkMode ? styles.dark : ''}`} ref={modalRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {isEditing ? 'Görevi Düzenle' : 'Yeni Görev Oluştur'}
          </h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className={styles.modalBody}>
          {/* Title */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Görev Başlığı *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={styles.input}
              placeholder="Görev başlığını girin..."
              autoFocus
            />
          </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Açıklama</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              placeholder="Görev açıklamasını girin..."
              rows={4}
            />
          </div>

          {/* Priority & Status Row */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Öncelik</label>
              <select name="priority" value={formData.priority} onChange={handleChange} className={styles.select}>
                <option value="low">Düşük</option>
                <option value="medium">Orta</option>
                <option value="high">Yüksek</option>
              </select>
            </div>
            {isEditing && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Durum</label>
                <select name="status" value={formData.status} onChange={handleChange} className={styles.select}>
                  <option value="pending">Beklemede</option>
                  <option value="in-progress">Devam Ediyor</option>
                  <option value="completed">Tamamlandı</option>
                  <option value="cancelled">İptal Edildi</option>
                </select>
              </div>
            )}
          </div>

          {/* Due Date */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Son Tarih</label>
            <input
              type="date"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleChange}
              className={styles.input}
            />
          </div>

          {/* Assignees */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Atanan Kişiler *</label>
            
            {/* Selected assignees */}
            {selectedAssignees.length > 0 && (
              <div className={styles.selectedAssignees}>
                {selectedAssignees.map(a => (
                  <span key={a.id} className={styles.assigneeChip}>
                    {a.name}
                    <button type="button" onClick={() => removeAssignee(a.id)} className={styles.chipRemove}>
                      <FaTimes />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search & Dropdown */}
            <div className={styles.assigneeSearchWrapper}>
              <FaSearch className={styles.assigneeSearchIcon} />
              <input
                type="text"
                className={styles.assigneeSearchInput}
                placeholder="Çalışan ara..."
                value={assigneeSearch}
                onChange={(e) => setAssigneeSearch(e.target.value)}
                onFocus={() => setShowAssigneeDropdown(true)}
              />
            </div>

            {showAssigneeDropdown && (
              <div className={styles.assigneeDropdown}>
                {filteredEmployees.length === 0 ? (
                  <div className={styles.noResults}>Sonuç bulunamadı</div>
                ) : (
                  filteredEmployees.map(emp => {
                    const isSelected = selectedAssignees.some(a => a.id === emp.id);
                    return (
                      <div
                        key={emp.id}
                        className={`${styles.assigneeOption} ${isSelected ? styles.assigneeSelected : ''}`}
                        onClick={() => toggleAssignee(emp)}
                      >
                        <span>{emp.eName} {emp.eSurname}</span>
                        {isSelected && <span className={styles.checkMark}>✓</span>}
                      </div>
                    );
                  })
                )}
                <button
                  type="button"
                  className={styles.closeDropdownBtn}
                  onClick={() => setShowAssigneeDropdown(false)}
                >
                  Kapat
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              İptal
            </button>
            <button type="submit" className={styles.saveBtn} disabled={isSaving}>
              {isSaving ? (
                <span className={styles.savingSpinner} />
              ) : (
                <>
                  <FaSave />
                  <span>{isEditing ? 'Güncelle' : 'Oluştur'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTaskModal;
